// Custom element for double exposure effect
// Dependencies: rft-double-exposure.css
// Version: 1.0.0

class RftVariantSelector extends HTMLElement {
  constructor() {
    super();
    this.parentSelector = "product-card";
    this.imageTargetSelector = ".product-card__media .product-card__image--primary";
    this.imageSecondarySelector = ".product-card__media .product-card__image--secondary";
    this.groupedFieldSelector = "gemstone color";
    this.productUrlSelectors = [".product-card__media", ".product-title"];
  }

  connectedCallback() {
    this.loadData();
    this.renderContainer();
    this.renderSwatches();
  }

  // get All information from non-visible elements ( noscript )
  loadData() {
    const noscript = this.querySelector("noscript");
    this.productData = {};
    if (noscript) {
      this.productData = JSON.parse(noscript.textContent);
    }
  }

  // Render container
  renderContainer() {
    this.innerHTML = `<div class="rft-swatches"></div><label class="rft-label">${this?.productData?.selected?.name || ''}</label>`;
  }

  // Render swatches
  renderSwatches() {
    let options = [];
    const container = this.querySelector(".rft-swatches");

    // Find in tags if group by exist in tags1. collection-swatch-group-by
    const tags = this.productData.tags1 || this.productData.tags || [];
    if (tags.length === 0) {
      console.log("Product has no tags, using default groupedFieldSelector:", this.productData);
    }
    const groupByTag = tags.find((tag) => tag.includes("collection-swatch-group-by"));
    if (groupByTag) {
      this.groupedFieldSelector = groupByTag.replace("collection-swatch-group-by:", "").trim().toLowerCase();
    }

    // count color options without duplicates
    this.colorCount = 0;
    let variants = this.productData.skus || this.productData.variants || [];
    this.productData?.options?.forEach((option, index) => {
      if (option?.name?.toLowerCase() === this.groupedFieldSelector) {
        this.groupedFieldSelector = `option${index + 1}`;
      }
    });
    // reorder Variants to selected first
    variants = variants.sort((a, b) => {
      if (a.id === parseInt(this.dataset.selectedVariantId)) return -1;
      if (b.id === parseInt(this.dataset.selectedVariantId)) return 1;
      return 0;
    });
    variants.forEach((variant) => {
      const swatch = document.createElement("button");
      swatch.classList.add("rft-swatch");
      let colorOption = variant?.customFields?.find((field) => field.key === this.groupedFieldSelector);
      if (!colorOption) {
        colorOption = variant[this.groupedFieldSelector] || '';
      }
      if (!colorOption || options.includes(colorOption)) {
        return;
      }
      if (this.colorCount >= 4) {
        options.push(colorOption);
        this.colorCount++;
        return;
      }
      this.colorCount++;
      // Set default selected variant
      if (this.productData && this.productData?.selected?.id !== parseInt(this.dataset.selectedVariantId)) {
        const selected_variant = this.productData.variants.find((v) => v.id === parseInt(this.dataset.selectedVariantId));
        const main_image = selected_variant?.main_image?.src || selected_variant?.featured_image?.src || variant?.featured_image?.src || "";
        this.productData.selected = {
          id: selected_variant?.id || variant.id,
          name: colorOption,
          image: main_image
        };

        this.updateProductUrl(selected_variant?.id || variant.id, selected_variant[this.groupedFieldSelector] || colorOption);
        this.updateProductImage(main_image);
      }
      options.push(colorOption);
      swatch.classList.add(`rft-swatch--${colorOption?.toLowerCase()?.replace(/\s/g, "-")}`);
      // create css var for color
      swatch.style.setProperty("--rft-swatch-color", `linear-gradient(to right, ${colorOption?.toLowerCase()}, ${colorOption?.toLowerCase()})`);
      Object.assign(swatch.dataset, {
        variant: variant.id,
        color: colorOption,
        image: variant?.featured_image?.src || variant?.main_image.src || "",
        available: variant.is_available ? "true" : "false",
      });
      this.attachSwatchEvents(swatch);

      // ADA accessibility
      swatch.setAttribute("aria-label", `${this.productData.title} - ${colorOption}${variant.available ? '' : ' - Out of stock'}`);
      if (variant.id === this.productData.selected.id) {
        swatch.setAttribute("aria-current", "true");
      }
      if (variant.available) {
        swatch.setAttribute("tabindex", "0");
      } else {
        swatch.setAttribute("aria-disabled", "true");
        swatch.setAttribute("tabindex", "-1");
        swatch.classList.add("rft-swatch--unavailable");
      }

      container.appendChild(swatch);
      this.updateSwatches();
    });

    // Remove ?variant if only one color option
    if (this.colorCount <= 1) {
      this.updateProductUrl();
    }

    // if this.colorCount > 4, add +count label
    if (this.colorCount > 4) {
      const swatch = document.createElement("button");
      swatch.classList.add("rft-swatch");
      swatch.classList.add("rft-swatch--more");
      swatch.textContent = `+${this.colorCount - 4}`;
      container.appendChild(swatch);
    }

    // Current Label Name
  }

  attachSwatchEvents(swatch) {
    swatch.addEventListener("click", () => {
      if (swatch.dataset.available === "false") {
        return;
      }

      // Update selected variant
      this.productData.selected = {
        id: parseInt(swatch.dataset.variant),
        name: swatch.dataset.color,
        image: swatch.dataset.image,
      };

      this.updateSwatches();
      this.updateProductImage(swatch.dataset.image);
      this.updateProductUrl();
    });
    // On hover, change image and reset on mouseout
    swatch.addEventListener("mouseover", () => {
      if (swatch.dataset.available === "false") {
        return;
      }
      this.updateProductImage(swatch.dataset.image);
    });
    swatch.addEventListener("mouseout", () => {
      if (swatch.dataset.available === "false") {
        return;
      }
      this.updateProductImage(this.productData.selected.image);
    });
  }

  updateProductUrl(id = null, selectedName = null) {
    const url = this.productData.handle;

    this.productUrlSelectors.forEach((selector) => {
      const productUrl = this.closest(this.parentSelector).querySelector(selector);
      // Only add variant parameter if there are more than 1 color options
      if (this.colorCount >= 1) {
        productUrl.href = `/products/${url}?variant=${id || this.productData.selected.id || ""}`;
      } else {
        productUrl.href = `/products/${url}`;
      }
    });
    this.querySelector(".rft-label").textContent = this.productData.selected
      ? selectedName || this.productData.selected.name
      : "";
  }

  updateProductImage(imageUrl) {
    if (!imageUrl) {
      return;
    }
    const imageOptimized = this.optimizeImage(imageUrl);
    const target = this.closest(this.parentSelector).querySelector(this.imageTargetSelector);
    const targetSecondary = this.closest(this.parentSelector).querySelector(this.imageSecondarySelector);
    if (targetSecondary) {
      targetSecondary.src = imageOptimized;
      targetSecondary.removeAttribute("srcset");
    }
    target.src = imageOptimized;
    target.removeAttribute("srcset");
  }

  updateSwatches() {
    const swatches = this.querySelectorAll(".rft-swatch");
    swatches.forEach((swatch) => {
      if (parseInt(swatch.dataset.variant) === this.productData.selected.id) {
        swatch.classList.add("rft-swatch--selected");
      } else {
        swatch.classList.remove("rft-swatch--selected");
      }
    });
  }

  optimizeImage(imageUrl) {
    // remove query parameters
    imageUrl = imageUrl.replace(/\?.+/, "");
    // get extension
    const extension = imageUrl.split(".").pop();
    imageUrl = imageUrl.replace(`.${extension}`, `_650x650.${extension}`);

    return imageUrl;
  }
}

customElements.define("rft-variant-selector", RftVariantSelector);
